// P4 AI Tutor -- pure DTO <-> domain mapping (08 §8, ACCEPTED/FROZEN architecture).
// No network access, no Vuex access. Every function here must be trivially
// unit-testable against static fixtures.

/**
 * @param {import('./dto').TutorAsk|null|undefined} ask
 * @returns {import('./domain').TutorPendingAction|null}
 */
function toTutorPendingAction(ask) {
  if (!ask) return null
  return {
    kind: ask.kind,
    question: ask.question,
    options: ask.options ?? null,
    draft: ask.draft ?? null,
  }
}

/**
 * AT-API-020-H1 (API 0.2.0, migration reference §8): a session is `id,
 * courseId, clientId, pinnedSourceId, currentNodeId, currentNodeTitle,
 * status, turnCount, lastTurnAt, createdAt`. A turn never pauses any more,
 * so there is no session-level question to answer: `pendingAction` is always
 * null (the field stays so the chat components keep their shape), and a
 * leftover `pendingAsk` is never surfaced as a card nobody could answer.
 * @param {import('./dto').TutorSessionResponseDto} dto
 * @returns {import('./domain').TutorSession}
 */
export function toTutorSession(dto) {
  return {
    id: dto.id,
    status: dto.status,
    topicLabel: dto.currentNodeTitle ?? null,
    turnCount: dto.turnCount,
    createdAt: dto.createdAt,
    lastTurnAt: dto.lastTurnAt ?? null,
    pendingAction: null,
  }
}

// AT-API-020-H1 -- the 0.2.0 action id grammar (migration reference §1):
// lowercase Latin letters, digits and `_`, starting with a letter, at most 40
// characters. The same rule Subway enforces before any lease.
const ACTION_ID_RE = /^[a-z][a-z0-9_]{0,39}$/

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isActionId(value) {
  return typeof value === 'string' && ACTION_ID_RE.test(value)
}

/**
 * AT-API-020-H1 (doc 34/01 §3.2) -- the course's Tutor catalog as Subway
 * returns it: `{ actions: [{ id, label, prompt }], teacherHandoff }`. Subway
 * already normalizes it; this stays defensive so nothing malformed can become
 * an action Borna would send: an entry without a valid id, a label or a
 * string prompt is dropped, a repeated id keeps its first entry, anything
 * that is not an object is an empty catalog (a valid answer: the course
 * offers no actions and every turn travels as text).
 * @param {unknown} dto
 * @returns {import('./domain').TutorCatalog}
 */
export function toTutorCatalog(dto) {
  const record =
    dto && typeof dto === 'object' && !Array.isArray(dto) ? dto : {}
  const actions = []
  if (Array.isArray(record.actions)) {
    record.actions.forEach(item => {
      if (!item || typeof item !== 'object') return
      const { id, label, prompt } = item
      if (
        !isActionId(id) ||
        typeof label !== 'string' ||
        !label.trim() ||
        typeof prompt !== 'string'
      ) {
        return
      }
      if (actions.some(action => action.id === id)) return
      actions.push({ id, label, prompt })
    })
  }
  return {
    actions,
    teacherHandoff:
      typeof record.teacherHandoff === 'boolean' ? record.teacherHandoff : true,
  }
}

/**
 * AT-API-020-H1 (doc 34/01 §3.3, doc 34/02 §3) -- the action Borna may send:
 * the wanted id when the catalog READ FOR THAT COURSE lists it, otherwise
 * null (the turn then travels as text only). An absent, empty or failed
 * catalog therefore never produces an action.
 * @param {string|null|undefined} actionId
 * @param {import('./domain').TutorCatalog|null|undefined} catalog
 * @returns {string|null}
 */
export function resolveActionId(actionId, catalog) {
  if (!isActionId(actionId)) return null
  const actions =
    catalog && Array.isArray(catalog.actions) ? catalog.actions : []
  return actions.some(action => action && action.id === actionId)
    ? actionId
    : null
}

/**
 * Maps EVERY `citedLocators` entry -- never just the first one (08 §8 binding
 * correction). An empty/absent array maps to `[]`.
 * @param {import('./dto').TutorCitedLocator[]|undefined} citedLocators
 * @returns {import('./domain').TutorCitation[]}
 */
function toCitations(citedLocators) {
  if (!Array.isArray(citedLocators)) return []
  return citedLocators.map(loc => ({
    sourceId: loc.sourceId,
    printedLabel: loc.printedLabel ?? null,
    locator: loc.locator ?? null,
  }))
}

/**
 * Maps already-uploaded student image URLs into domain attachments. The 0.1
 * `pageImageUrls` (source-page images, a different concept) was never an
 * attachment and, since AT-API-020-H3, is not carried at all: 0.2.0 never
 * sends it, and the sources of a reply are its `citations` (`toSources`).
 * @param {import('./dto').TutorTurnResponseDto} dto
 * @returns {import('./domain').TutorAttachment[]}
 */
function toAttachments(dto) {
  if (!Array.isArray(dto.attachmentUrls)) return []
  return dto.attachmentUrls
    .filter(url => typeof url === 'string' && url)
    .map(url => ({
      kind: 'image',
      status: 'uploaded',
      previewUrl: url,
    }))
}

/**
 * AT-API-020-H3 (API 0.2.0, migration reference §4; doc 34/03 §7) -- the
 * sources the reply rests on, as the chat shows them: `n` is the number of
 * the `[^n]` marker in the text, then the source's title, the page as the
 * book prints it and the item (exercise / example) the passage names. An
 * entry needs an integer `n` of at least 1; the first entry wins for a
 * repeated `n`; strings are trimmed, anything empty or not a string is
 * null, an item with nothing in it is null. The passage and source
 * identifiers are not carried: nothing renders them. A missing or malformed
 * list is `[]`.
 * POST-UAT-UX / PUX-A (doc 35/01 §1): the cited page's signed image
 * (`pageImageUrl`) is kept as `image` -- only when it is a safe URL
 * (`safePageImageUrl`), and only then is the key present, so a source
 * without an image has exactly the H3 shape. It is the citation viewer's,
 * never text.
 * @param {import('./dto').TutorCitationDto[]|undefined} citations
 * @returns {import('./domain').TutorSource[]}
 */
function toSources(citations) {
  if (!Array.isArray(citations)) return []
  const sources = []
  citations.forEach(entry => {
    if (!entry || typeof entry !== 'object') return
    const { n } = entry
    if (!Number.isInteger(n) || n < 1) return
    if (sources.some(source => source.n === n)) return
    const source = {
      n,
      title: cleanText(entry.sourceTitle),
      page: cleanText(entry.page),
      item: toSourceItem(entry.item),
    }
    const image = safePageImageUrl(entry.pageImageUrl)
    if (image) source.image = image
    // 36 TC-3 (doc 36/01 §4): the provider's stable source identity, kept
    // only to group the Sources rows (same book + same printed page). Never
    // rendered; absent when missing or malformed, so such a citation is
    // never merged with another one.
    const sourceId = toSourceId(entry.sourceId)
    if (sourceId) source.sourceId = sourceId
    sources.push(source)
  })
  return sources
}

const SOURCE_ID_MAX = 128

function toSourceId(value) {
  if (typeof value !== 'string') return null
  const id = value.trim()
  return id && id.length <= SOURCE_ID_MAX ? id : null
}

/**
 * 36_TUTOR_POST_UAT_CORRECTIONS TC-2 (doc 36/01 §3) -- the Learn Setup's
 * chapter options from Subway's outline read: `{ nodeId, title, group }` in
 * the order Subway gives (by the provider's `order`). An entry needs a
 * non-empty string id and title; duplicates and anything malformed are
 * dropped; a missing or malformed body is `[]`.
 * @param {import('./dto').CourseOutlineDto|undefined} dto
 * @returns {import('./domain').CourseChapter[]}
 */
export function toCourseChapters(dto) {
  const list = dto && Array.isArray(dto.chapters) ? dto.chapters : []
  const chapters = []
  list.forEach(entry => {
    if (!entry || typeof entry !== 'object') return
    const nodeId = cleanText(entry.nodeId)
    const title = cleanText(entry.title)
    if (!nodeId || !title) return
    if (chapters.some(chapter => chapter.nodeId === nodeId)) return
    chapters.push({ nodeId, title, group: cleanText(entry.group) })
  })
  return chapters
}

// A signed storage URL is long (SigV4 query) but bounded.
const PAGE_IMAGE_URL_MAX = 4096

/**
 * POST-UAT-UX / PUX-A -- the page image URL of a citation, or null: a string
 * of at most 4096 characters that parses as an absolute `https:` URL with no
 * user-info. Anything else (http, javascript:, data:, relative, credentials
 * in the authority) is never handed to an `<img>`. The origin itself is the
 * runtime CSP's to enforce (`img-src`, HIGH_LINE_AI_TUTOR_MEDIA_ORIGIN).
 * @param {*} value
 * @returns {string|null}
 */
export function safePageImageUrl(value) {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (!raw || raw.length > PAGE_IMAGE_URL_MAX) return null
  let url
  try {
    url = new URL(raw)
  } catch (err) {
    return null
  }
  if (url.protocol !== 'https:' || url.username || url.password) return null
  return url.href
}

function cleanText(value) {
  if (typeof value !== 'string') return null
  const text = value.trim()
  return text ? text : null
}

function toSourceItem(item) {
  if (!item || typeof item !== 'object') return null
  const kind = cleanText(item.kind)
  const label = cleanText(item.label)
  const set = cleanText(item.set)
  return kind || label || set ? { kind, label, set } : null
}

// AT-API-020-H1 (API 0.2.0, migration reference §4 "Suggestions") -- the
// suggested next turns are `{ label, text }`: `label` is what the card shows,
// `text` is what the Student's message says when they pick it, sent as an
// ordinary turn (never an id the server resolves). There is no `id` and no
// `kind` on the wire any more:
//   - `id` here is LOCAL, for rendering only (the list key, the "selected"
//     look while the pick is on its way) -- position in the turn's own list.
//     It is never sent anywhere;
//   - `kind` is kept when a known value is present and is `unknown`
//     otherwise, which the card already labels neutrally.
// A suggestion needs a label and a text to send; one without either is not
// offered (a card that would send nothing, or show nothing).
const SUGGESTION_KINDS = [
  'next',
  'check',
  'deeper',
  'broader',
  'apply',
  'clarify',
]

/**
 * @param {import('./dto').TutorSuggestionDto[]|undefined} suggestions
 * @returns {import('./domain').TutorFollowUpSuggestion[]}
 */
function toSuggestions(suggestions) {
  if (!Array.isArray(suggestions)) return []
  return suggestions
    .filter(
      item =>
        item &&
        typeof item === 'object' &&
        typeof item.label === 'string' &&
        item.label.trim() &&
        typeof item.text === 'string' &&
        item.text.trim()
    )
    .map((item, index) => ({
      id: `s${index + 1}`,
      kind: SUGGESTION_KINDS.includes(item.kind) ? item.kind : 'unknown',
      label: item.label.trim(),
      text: item.text.trim(),
    }))
}

// FINAL CHAT V2 -- figures drawn in a reply (upstream 0a347ba, design A15).
// The reply text itself carries each one as a ```figure block; these are the
// stored specs, which add what the text cannot (a book figure's signed page
// image). Anything without a kind and an alt text is not a figure.
function toFigures(figures) {
  if (!Array.isArray(figures)) return []
  return figures.filter(
    item =>
      item &&
      typeof item === 'object' &&
      typeof item.kind === 'string' &&
      typeof item.alt === 'string'
  )
}

/**
 * AT-API-020-H1 (API 0.2.0, migration reference §4) -- every field beyond
 * `id` / `role` is optional for Borna: a reply may come with or without
 * `citations`, `suggestions`, `assessment`, `figures`, `attachmentUrls`,
 * `move`, `violations`, `timings`, and a stored 0.1 turn comes without the
 * fields the provider removed (`ask`, `verdict`, `citedLocators`,
 * `transcription`, `pageImageUrls`). Nothing absent may break the mapping;
 * nothing unknown is carried into the domain. `assessment` is deliberately
 * not mapped (doc 34/07 D-04: not rendered). AT-API-020-H3: the 0.2.0
 * `citations` list is mapped to `sources` (`toSources`); its `[^n]` markers
 * stay in `content`, where the renderer draws them as small numbers.
 * @param {import('./dto').TutorTurnResponseDto} dto
 * @returns {import('./domain').TutorTurn}
 */
export function toTutorTurn(dto) {
  return {
    id: dto.id,
    sessionId: dto.sessionId,
    role: dto.role,
    // A button-only turn may be stored with no content (reference §3).
    content: typeof dto.content === 'string' ? dto.content : '',
    citations: toCitations(dto.citedLocators),
    // Capability defaults, per capabilities.js / 08 §10 -- the ONLY place
    // that changes when the backend ships a confirmed contract for either.
    // FINAL CHAT V2: suggestions are confirmed (upstream 0a347ba).
    followUpSuggestions: toSuggestions(dto.suggestions),
    practiceQuestion: null,
    verdict: dto.verdict ?? null,
    attachments: toAttachments(dto),
    // AT-API-020-H3 -- the sources the reply rests on (0.2.0 `citations`),
    // shown under the reply; the 0.1 book pages (`pageImageUrls`) are gone.
    sources: toSources(dto.citations),
    figures: toFigures(dto.figures),
    // AI Tutor streaming presentation (doc 27 §6.4): the turn a tutor reply
    // answers -- the SSE `turnId` of its live events. Already received;
    // mapped so the presentation layer can tell which persisted turn is the
    // reply it is showing.
    replyToTurnId: dto.replyToTurnId ?? null,
    // STUDENT CHAT V2 -- CURRENT CONTRACT AUDIT: a tutor turn that carries an
    // `ask` is the tutor asking the student something (disambiguate/clarify/
    // confirm, and since upstream cdc0feb `intake`); its `content` is the
    // question itself. Persisted on the turn, so it survives refetch/resume.
    // AT-API-020-H1: under 0.2.0 no turn carries one (always null); the read
    // is kept only so a stored turn that still has it renders as history.
    // Nothing can answer it -- the `resume` path is gone.
    ask: toTutorPendingAction(dto.ask),
    // CHAT-UX-01 (doc 29 §5.2): only WHETHER this tutor reply was made from a
    // photo reading the student had resolved -- upstream sets `transcription`
    // on a reply exactly when the paused image turn was resumed (confirmed or
    // corrected), and never on a reply to a fresh message. The reading itself
    // stays unmapped (dto.js); what the student confirmed is `ask.draft`.
    fromImageReading: !!dto.transcription,
    createdAt: dto.createdAt,
  }
}

/**
 * CAT-1A (doc 07 §12.1, H-3, human-authorized 2026-09-26) -- the additive
 * `createSession` body: the client-generated request id (Borna-side
 * idempotency) and the onboarding snapshot Borna keeps in its conversation
 * catalog. Only the values the V2 setup actually collects travel; anything
 * absent is simply omitted. With no context and no request id this is still
 * `{}` -- exactly what every client sent before CAT-1A, which Subway keeps
 * accepting. Nothing here reaches Synapse: Subway stores the snapshot and
 * still sends `{}` upstream.
 * @param {import('./domain').TutorSetupContext|null|undefined} [input]
 * @param {string|null} [clientRequestId]
 * @returns {import('./dto').CreateTutorSessionDto}
 */
export function toCreateSessionDto(input, clientRequestId) {
  const dto = {}
  if (clientRequestId) dto.clientRequestId = clientRequestId
  const snapshot = {}
  if (input && typeof input === 'object') {
    if (input.intent) snapshot.intent = input.intent
    if (input.grade) snapshot.grade = input.grade
    if (input.studyField) snapshot.studyField = input.studyField
    if (input.topic) snapshot.topic = input.topic
    // 36 TC-2: a Learn topic picked from the course outline names its node,
    // which Subway checks against the Student's outline (the stored title is
    // then the provider's). A typed topic carries none.
    if (input.intent === 'learn' && input.topic && input.topicNodeId) {
      snapshot.topicNodeId = input.topicNodeId
    }
    if (input.stuckReason) snapshot.stuckReason = input.stuckReason
    if (input.proficiency) snapshot.proficiency = input.proficiency
  }
  if (Object.keys(snapshot).length > 0) dto.snapshot = snapshot
  return dto
}

// AT-API-020-H1 (doc 34/07 D-04, doc 34/02 §3) -- the three Setup intentions
// are kept exactly as the Student sees them; each names the 0.2.0 catalog
// action Borna asks for with the conversation's first turn. Whether that
// action is really sent is decided against the course's catalog
// (`resolveActionId`): an id the course does not list is never sent.
//
// The 0.1 `intake` answers (the troubleshoot / learn sub-choices) have no
// place in the 0.2.0 request: they stay Borna's own setup record (the
// creation snapshot, the context bar) and are not sent to the Tutor -- Borna
// sends no `context` (doc 34/07 D-09; a later unit, if ever authorized).
const INTENT_ACTIONS = {
  troubleshoot: 'solve_exercise',
  review: 'check_answer',
  learn: 'explain_topic',
}

/**
 * @param {import('./domain').TutorSetupContext|null|undefined} setupContext
 * @returns {{actionId:string}|null}
 */
export function toTutorRequest(setupContext) {
  const actionId = setupContext && INTENT_ACTIONS[setupContext.intent]
  return actionId ? { actionId } : null
}

/**
 * AT-API-020-H1 (doc 34/01 §3.1) -- the 0.2.0 turn request, as a WHITELIST:
 * `text`, an optional `actionId` and `attachments`, plus Borna's own
 * `boardShare`. Nothing else is ever built here -- no `mode`, `intake`,
 * `suggestionId`, `resume` or `context`, whatever the caller passes (Subway
 * drops them too; Borna simply never writes them). An `actionId` that is not
 * a valid action id is left out rather than sent.
 *
 * WB-M2A (doc 31 §7): `boardShare`, the Borna-only descriptor of a board
 * picture ({shareId, scope, elementIds, bounds, sceneVersion,
 * boardVersion}). It travels to Subway beside the ordinary fields and is
 * stripped there before the turn goes upstream; it is only ever sent with
 * exactly one attachment and a text.
 * @param {{text?:string, actionId?:string|null,
 *   attachments?: import('./domain').TutorAttachment[],
 *   boardShare?: object}} input
 * @returns {import('./dto').CreateTutorTurnDto}
 */
export function toCreateTurnDto(input = {}) {
  const dto = {}
  if (typeof input.text === 'string') dto.text = input.text
  if (isActionId(input.actionId)) dto.actionId = input.actionId
  if (Array.isArray(input.attachments) && input.attachments.length > 0) {
    const attachments = input.attachments
      .filter(attachment => attachment.remoteFilename)
      .map(attachment => ({
        type: 'student_image',
        filename: attachment.remoteFilename,
      }))
    if (attachments.length > 0) dto.attachments = attachments
  }
  if (
    input.boardShare &&
    typeof input.boardShare === 'object' &&
    typeof input.boardShare.shareId === 'string' &&
    dto.attachments &&
    dto.attachments.length === 1 &&
    typeof dto.text === 'string' &&
    dto.text.trim()
  ) {
    const share = input.boardShare
    dto.boardShare = {
      shareId: share.shareId,
      scope: share.scope,
      elementIds: Array.from(share.elementIds || []),
      bounds: { ...(share.bounds || {}) },
      sceneVersion: share.sceneVersion,
      boardVersion: Number.isInteger(share.boardVersion)
        ? share.boardVersion
        : null,
    }
  }
  return dto
}

// WB-M2A: the conversation read's share records, by Student turn id.
export { toBoardShareByTurnId } from './boardShare'

/**
 * Folds one `SessionEvent` into the current `TutorProcessingState` (08 §8,
 * mirroring Track B §9's `LiveReply` behavior exactly):
 * - `state`  -> `stage:'unknown'`, except for a value in `KNOWN_STAGES`,
 *   which is kept; any other value is still bucketed and never shown as if
 *   it had a defined meaning (OQ-06). Text buffer preserved only if `turnId`
 *   matches -- so `recording`, which arrives once the reply's text is
 *   complete, keeps that text on screen.
 * - `text`   -> appended to the buffer keyed by `turnId`; a `turnId` change
 *   mid-stream drops the previous partial text, it does not merge it.
 * - `done`   -> `{phase:'done'}`; the event's own `turn` payload stays unread
 *   (PU-008, matching the Mock's proven behavior).
 * - `error`  -> `{phase:'error', error: normalizeTutorError(event)}`.
 *
 * AT-API-020-H1 (API 0.2.0, migration reference §5; AT-API-020-SUP-5): the
 * stream carries four kinds of event -- `state`, `text`, `done`, `error` --
 * and `state` is one of `reading`, `preparing`, `answering`, `recording`.
 * There is no `ask` event: a turn never pauses. Anything else falls into the
 * unrecognized bucket below.
 * @param {import('./domain').TutorProcessingState|null|undefined} state
 * @param {import('./dto').SessionEvent} event
 * @returns {import('./domain').TutorProcessingState}
 */
export const KNOWN_STAGES = Object.freeze([
  'reading',
  'preparing',
  'answering',
  'recording',
])

export function applySessionEvent(state, event) {
  const current = state || { phase: 'idle' }
  const sameTurn = current.turnId === event.turnId
  const priorText = sameTurn ? current.text || '' : ''

  switch (event.type) {
    case 'state':
      return {
        phase: 'processing',
        stage: KNOWN_STAGES.includes(event.state) ? event.state : 'unknown',
        turnId: event.turnId,
        text: priorText,
      }
    case 'text':
      return {
        phase: 'streaming',
        turnId: event.turnId,
        text: priorText + event.text,
      }
    case 'done':
      return { phase: 'done' }
    case 'error':
      return { phase: 'error', error: normalizeTutorError(event) }
    default:
      // Unrecognized event type -- bucketed the same way an unrecognized
      // `state` value is, never surfaced as if it had defined meaning.
      return {
        phase: 'processing',
        stage: 'unknown',
        turnId: event.turnId,
        text: priorText,
      }
  }
}

/**
 * Normalizes any failure (an `api.js` ApiError, an SSE `error` event, or a
 * generic network/stream Error) into the small closed `TutorError` union
 * (08 §8/§14). Components branch on `.kind` only, never on raw HTTP status.
 * @param {unknown} err
 * @returns {import('./domain').TutorError}
 */
export function normalizeTutorError(err) {
  if (!err || typeof err !== 'object') {
    return { kind: 'unknown', message: 'An unknown error occurred' }
  }

  if (typeof err.status === 'number') {
    return apiErrorToTutorError(err)
  }

  // P4 AI Tutor WP-02C-2 (doc 11 §B) -- an already TutorError-shaped local
  // failure, e.g. the aiTutor store's own "A course must be selected" guard
  // thrown before any network call. Passed through as-is rather than falling
  // into the generic `unknown` bucket below.
  if (typeof err.kind === 'string' && typeof err.message === 'string') {
    return { kind: err.kind, message: err.message, code: err.code ?? null }
  }

  if (err.type === 'error' && typeof err.message === 'string') {
    // An SSE `error` event (Track B §9) -- a dropped/errored stream, not an
    // HTTP failure.
    return { kind: 'stream_interrupted', message: err.message }
  }

  if (err instanceof Error) {
    return { kind: 'network', message: err.message || 'Network error' }
  }

  return { kind: 'unknown', message: 'An unknown error occurred' }
}

// AT-API-020-H1 (doc 34/01 §3.3) -- Subway's stable code for a turn whose
// `actionId` is not one of the course's actions any more (upstream
// `tutor.unknown_action`, refused before any student turn is saved: nothing
// was charged). It arrives as a 422 with this `code`; the failure `kind`
// stays the generic one, and the store reads the code.
export const ACTION_REJECTED_CODE = 'AI_TUTOR_ACTION_REJECTED'

// CR-03 -- Subway's stable Student AI Tutor credit codes (CR-02) and the
// TutorError kinds they become.
const CREDIT_ERROR_KINDS = {
  AI_TUTOR_CREDITS_EXHAUSTED: 'credits_exhausted',
  AI_TUTOR_TURN_IN_PROGRESS: 'turn_in_progress',
}

/**
 * P4 AI Tutor WP-02C-2 (doc 10, doc 11 §F/§J) -- Subway's actual, verified
 * error envelope (`server/router/res-error.ts`) is `{ err: string, code?:
 * string }`, not the `{ code, message }` shape the AI Tutor upstream itself
 * uses. `body.err` is read first (Subway's shape); `body.message` is kept as
 * a fallback for any caller still hitting the AI Tutor backend directly
 * (the dormant WP-03-owned functions in api.js). `code` is passed through
 * additively so callers can distinguish e.g. `AI_TUTOR_COURSE_NOT_PROVISIONED`
 * from `LOCAL_COURSE_NOT_FOUND` without new `kind` values.
 * @param {import('./dto').ApiError} apiError
 * @returns {import('./domain').TutorError}
 */
function apiErrorToTutorError(apiError) {
  const { status, body } = apiError
  const bodyMessage =
    body && typeof body === 'object' ? body.err || body.message || null : null
  const code =
    body && typeof body === 'object' && typeof body.code === 'string'
      ? body.code
      : null

  // CR-03 (doc 28 §15/§18): the Student AI Tutor credit codes are read BEFORE
  // any status mapping -- by status alone a 402 is `unknown` and a 409 is
  // `not_found`, neither of which says what happened. Any other 402/409 keeps
  // the generic mapping below.
  if (code && CREDIT_ERROR_KINDS[code]) {
    return {
      kind: CREDIT_ERROR_KINDS[code],
      message: bodyMessage || code,
      code,
    }
  }

  if (status === 401 || status === 403) {
    return {
      kind: 'unauthorized',
      message: bodyMessage || 'Unauthorized',
      code,
    }
  }
  // 409 joins 404 here: Subway's AI_TUTOR_COURSE_NOT_PROVISIONED (doc 11 §J)
  // is a valid request blocked by current server-side state (no AI Tutor
  // Course provisioned yet for this local course) -- a not-found-shaped,
  // non-retryable outcome for the student, never an "unauthorized student"
  // message and never confused with AI Tutor's own 502 operator/integration
  // failures below.
  if (status === 404 || status === 409) {
    return { kind: 'not_found', message: bodyMessage || 'Not found', code }
  }
  if (status === 0) {
    return { kind: 'network', message: bodyMessage || 'Network error', code }
  }
  if (status >= 500) {
    return { kind: 'server', message: bodyMessage || 'Server error', code }
  }
  return { kind: 'unknown', message: bodyMessage || 'Request failed', code }
}
